import {DataTable} from '@/components-v2'
import '@/containers/Promotions/components/SelectionModalContent/ProductIdContent/ProductIdContent.styles.css'
import {useProductListForModal} from '@/hooks/promotion/useProductListForModal'
import {useDataTableV2} from '@/hooks/useDataTableV2'
import {productsTableHeader} from '@/services/MockData/Promotions/ProductTableColumns'
import {BuyType, COMBO_ID_TYPE, PRODUCT_ID_TYPE} from '@/utils/constants/promotionConstants'
import {comboProductsTableHeader} from '@/utils/productCombo/productComboConstants'
import {useCallback, useMemo} from 'react'

const ProductIdContentV2 = ({
  onSelection,
  combination,
  setCombination,
  isExclude,
  selectedValues = [],
  type = BuyType.PRODUCT_ID,
}) => {
  const isCombo = type === BuyType.COMBO_ID

  const {pageIndex, sorting, activeFilters, onPageIndexChange, sortingHandler, onColumnFiltersChange, handleResetAll} =
    useDataTableV2({
      defaultPageSize: 10,
      getRowId: row => row.productId,
    })

  const {products, isLoading} = useProductListForModal(activeFilters, sorting, pageIndex, type)

  const columns = useMemo(() => (isCombo ? comboProductsTableHeader : productsTableHeader), [isCombo])
  const tableData = useMemo(
    () => (products?.data ?? []).map(product => ({...product, id: String(product.productId)})),
    [products?.data]
  )

  const initialRowSelection = useMemo(() => {
    if (!selectedValues?.length) return {}
    return selectedValues.reduce((acc, sv) => ({...acc, [sv.id]: true}), {})
  }, [selectedValues])

  const rowSelectionStateUpdater = useCallback(
    selectedTableRows => {
      const rawRows = selectedTableRows.map(row => row.original ?? row)
      const values = rawRows
        .map(row => ({
          id: row.productId || '',
          displayText: row.productName || row.name || '',
        }))
        .filter(item => item.id)
      const currentIds = new Set((products?.data ?? []).map(row => String(row.productId)))
      const hiddenValues = selectedValues.filter(item => !currentIds.has(String(item.id)))
      const allValues = [...values, ...hiddenValues]

      onSelection({itemType: isCombo ? COMBO_ID_TYPE : PRODUCT_ID_TYPE, values: allValues})
    },
    [onSelection, isCombo, products, selectedValues]
  )

  const resetFilterButtonOptions = useMemo(
    () => ({
      onFiltersReset: () => {
        handleResetAll()
        onSelection({itemType: isCombo ? COMBO_ID_TYPE : PRODUCT_ID_TYPE, values: []})
      },
      text: 'Reset All',
    }),
    [handleResetAll, onSelection, isCombo]
  )

  return (
    <div className="product-id-content-wrapper">
      <DataTable
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        pageSize={10}
        totalRowsCount={products?.totalRecords || 0}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        sortingHandler={sortingHandler}
        onColumnFiltersChange={onColumnFiltersChange}
        rowSelectionStateUpdater={rowSelectionStateUpdater}
        selection="multiple"
        initialRowSelection={initialRowSelection}
        resetFilterButtonOptions={resetFilterButtonOptions}
        manualPagination={true}
        manualSorting={true}
        manualFiltering={true}
        showColumnFilters={true}
        size="small"
        maxHeight="60vh"
      />
    </div>
  )
}

export default ProductIdContentV2
